import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDocument } from "react-firebase-hooks/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc } from "firebase/firestore";
import { auth, firestore } from "@/firebase";
import { userConverter } from "@/converters";
import { LoginResult, UserType } from "@t3/shared";
import { Login } from "../pages/login";
import { useLoginUser } from "@/api/login";
import { LoginRequest } from "@t3/shared";
import {
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { useSearchParams, useLocation, Routes, Route } from "react-router-dom";
import { useCompanyConfigContext } from "./CompanyConfigContext";

import { LiteRaveClaim } from "@/pages/liteRaves/claim";

declare const window: any;

type AuthContextType = {
  user: UserType;
  callLoginUser: (request: LoginRequest) => Promise<LoginResult>;
  resultLoginUser: LoginResult | null;
  loadingLoginUser: boolean;
  errorLoginUser: string | null;
  signOut: () => Promise<void>;
  setResultLoginUser: (result: LoginResult | null) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [loginLoading] = useProcessEmailLink();
  const { companyConfig } = useCompanyConfigContext();
  const [
    callLoginUser,
    resultLoginUser,
    loadingLoginUser,
    errorLoginUser,
    setResultLoginUser,
  ] = useLoginUser();
  const [authUser, authUserLoading, authUserError] = useAuthState(auth);

  // Only set up the Firestore query when we have both authUser and companyConfig
  const companyId = companyConfig?.companyId;
  const userDocRef = authUser?.uid
    ? doc(firestore, `company/${companyId}/users/`, authUser.uid).withConverter(
        userConverter,
      )
    : null;

  const [userProfile, userProfileLoading, userProfileError] =
    useDocument(userDocRef);

  const user = userProfile?.data();

  if (authUserError || userProfileError) {
    console.error("Auth error:", authUserError || userProfileError);
    return <div>There was an authentication error. Please try again.</div>;
  }

  const loading =
    authUserLoading || userProfileLoading || loginLoading || loadingLoginUser;

  const contextValue: AuthContextType = {
    user: user!,
    callLoginUser,
    resultLoginUser,
    loadingLoginUser: loading,
    errorLoginUser,
    signOut: () => signOut(auth),
    setResultLoginUser,
  };

  if (loading || !authUser || !user) {
    return (
      <AuthContext.Provider value={contextValue}>
        <Routes>
          <Route path="/lite-raves/claim" element={<LiteRaveClaim />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useProcessEmailLink = () => {
  const [loading, setLoading] = useState(false);
  const signingIn = useRef(false);
  const [urlParams, setUrlParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    if (
      !signingIn.current &&
      isSignInWithEmailLink(auth, location.pathname + location.search)
    ) {
      signingIn.current = true;
      setLoading(true);
      //enforced by api
      signInWithEmailLink(auth, urlParams.get("e")!).finally(() => {
        // Create a new URLSearchParams object and filter out email link parameters
        const newParams = new URLSearchParams();
        // Firebase email link parameters to filter out
        const emailLinkParams = ["mode", "oobCode", "apiKey", "lang", "e"];

        urlParams.forEach((value, key) => {
          // Only keep parameters that aren't email link related
          if (!emailLinkParams.includes(key)) {
            newParams.append(key, value);
          }
        });
        setUrlParams(newParams);
        signingIn.current = false;
        setLoading(false);
      });
    }
  }, [location]);

  return [loading];
};
